import React, { useEffect, useMemo, useState } from "react";

const API = "/api";

const emptyItem = () => ({
    description: "",
    quantity: 1,
    rate: 0,
    cgstPercent: 0,
    sgstPercent: 0,
    igstPercent: 0
});

export default function Invoice() {

    const [clients, setClients] = useState([]);
    const [clientSearch, setClientSearch] = useState("");
    const [clientId, setClientId] = useState("");

    const [invoiceDate, setInvoiceDate] = useState(
        new Date().toISOString().split("T")[0]
    );

    const [dueDate, setDueDate] = useState("");
    const [discount, setDiscount] = useState(0);
    const [notes, setNotes] = useState("");

    // IMPORTANT: One item is present by default
    const [items, setItems] = useState([emptyItem()]);

    const [loadingClients, setLoadingClients] = useState(false);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");

    const companyId = 1;

    useEffect(() => {
        loadClients();
    }, []);

    async function loadClients() {
        try {
            setLoadingClients(true);
            setError("");

            const response = await fetch(
                `${API}/clients?companyId=${companyId}`
            );

            if (!response.ok) {
                throw new Error("Unable to load clients");
            }

            const data = await response.json();

            setClients(Array.isArray(data) ? data : []);

        } catch (err) {
            console.error(err);
            setError("Unable to load clients");
        } finally {
            setLoadingClients(false);
        }
    }

    const filteredClients = useMemo(() => {

        const search = clientSearch.trim().toLowerCase();

        if (!search) {
            return clients;
        }

        return clients.filter(client =>
            `${client.clientName || ""} ${client.email || ""} ${client.mobile || ""} ${client.gstin || ""}`
                .toLowerCase()
                .includes(search)
        );

    }, [clients, clientSearch]);

    function updateItem(index, field, value) {

        setItems(prev => {

            const updated = [...prev];

            updated[index] = {
                ...updated[index],
                [field]: value
            };

            return updated;
        });
    }

    function addItem() {
        setItems(prev => [...prev, emptyItem()]);
    }

    function removeItem(index) {

        if (items.length === 1) {
            setItems([emptyItem()]);
            return;
        }

        setItems(prev => prev.filter((_, i) => i !== index));
    }

    function calculateItem(item) {

        const quantity = Number(item.quantity) || 0;
        const rate = Number(item.rate) || 0;

        const taxable = quantity * rate;

        const cgst =
            taxable * (Number(item.cgstPercent) || 0) / 100;

        const sgst =
            taxable * (Number(item.sgstPercent) || 0) / 100;

        const igst =
            taxable * (Number(item.igstPercent) || 0) / 100;

        return {
            taxable,
            cgst,
            sgst,
            igst,
            total: taxable + cgst + sgst + igst
        };
    }

    const totals = useMemo(() => {

        let subtotal = 0;
        let tax = 0;

        items.forEach(item => {

            const calculated = calculateItem(item);

            subtotal += calculated.taxable;
            tax +=
                calculated.cgst +
                calculated.sgst +
                calculated.igst;
        });

        const discountAmount = Number(discount) || 0;

        let grandTotal =
            subtotal - discountAmount + tax;

        if (grandTotal < 0) {
            grandTotal = 0;
        }

        return {
            subtotal,
            tax,
            discount: discountAmount,
            total: grandTotal
        };

    }, [items, discount]);

    async function saveInvoice(e) {

        e.preventDefault();

        setMessage("");
        setError("");

        if (!clientId) {
            setError("Please select a client.");
            return;
        }

        const validItems = items
            .filter(item => item.description.trim() !== "")
            .map(item => ({
                description: item.description.trim(),
                quantity: Number(item.quantity) || 0,
                rate: Number(item.rate) || 0,
                cgstPercent: Number(item.cgstPercent) || 0,
                sgstPercent: Number(item.sgstPercent) || 0,
                igstPercent: Number(item.igstPercent) || 0
            }));

        if (validItems.length === 0) {
            setError("Please add at least one invoice item.");
            return;
        }

        /*
         * IMPORTANT:
         * Do NOT manually build JSON strings.
         * JSON.stringify() prevents the Unexpected token ']' error.
         */
        const payload = {
            companyId: Number(companyId),
            clientId: Number(clientId),
            invoiceDate,
            dueDate: dueDate || null,
            discountAmount: Number(discount) || 0,
            notes: notes || "",
            items: validItems
        };

        console.log("INVOICE PAYLOAD:", payload);

        try {

            setSaving(true);

            const response = await fetch(`${API}/invoices`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(payload)
            });

            const text = await response.text();

            let result;

            try {
                result = text ? JSON.parse(text) : {};
            } catch (jsonError) {
                console.error("Invalid server JSON:", text);
                throw new Error("Server returned invalid JSON.");
            }

            if (!response.ok) {
                throw new Error(
                    result.message ||
                    result.error ||
                    "Unable to create invoice"
                );
            }

            console.log("INVOICE CREATED:", result);

            setMessage(
                `Invoice ${result.invoiceNumber || ""} created successfully.`
            );

            setItems([emptyItem()]);
            setClientId("");
            setClientSearch("");
            setDiscount(0);
            setNotes("");

        } catch (err) {

            console.error("Invoice Error:", err);

            setError(
                err.message || "Unable to create invoice."
            );

        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="container-fluid py-4">

            <div className="d-flex justify-content-between align-items-center mb-4">

                <div>
                    <h3 className="fw-bold mb-1">
                        Create Invoice
                    </h3>

                    <div className="text-muted">
                        Create and send professional invoices to your clients
                    </div>
                </div>

                <div>
                    <span className="badge bg-dark px-3 py-2">
                        Potech Invoice
                    </span>
                </div>

            </div>

            {error && (
                <div className="alert alert-danger">
                    {error}
                </div>
            )}

            {message && (
                <div className="alert alert-success">
                    {message}
                </div>
            )}

            <form onSubmit={saveInvoice}>

                <div className="card shadow-sm border-0 mb-4">

                    <div className="card-header bg-white py-3">
                        <strong>Invoice Details</strong>
                    </div>

                    <div className="card-body">

                        <div className="row g-3">

                            <div className="col-md-5">

                                <label className="form-label fw-semibold">
                                    Client
                                </label>

                                <input
                                    type="text"
                                    className="form-control mb-2"
                                    placeholder="Search client..."
                                    value={clientSearch}
                                    onChange={e =>
                                        setClientSearch(e.target.value)
                                    }
                                />

                                <select
                                    className="form-select"
                                    value={clientId}
                                    onChange={e =>
                                        setClientId(e.target.value)
                                    }
                                >

                                    <option value="">
                                        {loadingClients
                                            ? "Loading clients..."
                                            : "Select Client"}
                                    </option>

                                    {filteredClients.map(client => (
                                        <option
                                            key={client.id}
                                            value={client.id}
                                        >
                                            {client.clientName}
                                            {client.mobile
                                                ? ` - ${client.mobile}`
                                                : ""}
                                        </option>
                                    ))}

                                </select>

                            </div>

                            <div className="col-md-3">

                                <label className="form-label fw-semibold">
                                    Invoice Date
                                </label>

                                <input
                                    type="date"
                                    className="form-control"
                                    value={invoiceDate}
                                    onChange={e =>
                                        setInvoiceDate(e.target.value)
                                    }
                                />

                            </div>

                            <div className="col-md-3">

                                <label className="form-label fw-semibold">
                                    Due Date
                                </label>

                                <input
                                    type="date"
                                    className="form-control"
                                    value={dueDate}
                                    onChange={e =>
                                        setDueDate(e.target.value)
                                    }
                                />

                            </div>

                        </div>

                    </div>

                </div>


                <div className="card shadow-sm border-0 mb-4">

                    <div className="card-header bg-white py-3 d-flex justify-content-between">

                        <strong>Invoice Items</strong>

                        <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={addItem}
                        >
                            + Add Item
                        </button>

                    </div>

                    <div className="card-body">

                        <div className="table-responsive">

                            <table className="table align-middle">

                                <thead>
                                    <tr>
                                        <th style={{ minWidth: 250 }}>
                                            Description
                                        </th>

                                        <th width="100">
                                            Qty
                                        </th>

                                        <th width="140">
                                            Rate
                                        </th>

                                        <th width="110">
                                            CGST %
                                        </th>

                                        <th width="110">
                                            SGST %
                                        </th>

                                        <th width="110">
                                            IGST %
                                        </th>

                                        <th width="150">
                                            Total
                                        </th>

                                        <th width="60"></th>
                                    </tr>
                                </thead>

                                <tbody>

                                    {items.map((item, index) => {

                                        const calculated =
                                            calculateItem(item);

                                        return (
                                            <tr key={index}>

                                                <td>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Service / Product description"
                                                        value={item.description}
                                                        onChange={e =>
                                                            updateItem(
                                                                index,
                                                                "description",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </td>

                                                <td>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.001"
                                                        className="form-control"
                                                        value={item.quantity}
                                                        onChange={e =>
                                                            updateItem(
                                                                index,
                                                                "quantity",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </td>

                                                <td>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        className="form-control"
                                                        value={item.rate}
                                                        onChange={e =>
                                                            updateItem(
                                                                index,
                                                                "rate",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </td>

                                                <td>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        className="form-control"
                                                        value={item.cgstPercent}
                                                        onChange={e =>
                                                            updateItem(
                                                                index,
                                                                "cgstPercent",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </td>

                                                <td>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        className="form-control"
                                                        value={item.sgstPercent}
                                                        onChange={e =>
                                                            updateItem(
                                                                index,
                                                                "sgstPercent",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </td>

                                                <td>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        className="form-control"
                                                        value={item.igstPercent}
                                                        onChange={e =>
                                                            updateItem(
                                                                index,
                                                                "igstPercent",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </td>

                                                <td className="fw-bold">
                                                    ₹{" "}
                                                    {calculated.total.toFixed(2)}
                                                </td>

                                                <td>

                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() =>
                                                            removeItem(index)
                                                        }
                                                    >
                                                        ×
                                                    </button>

                                                </td>

                                            </tr>
                                        );

                                    })}

                                </tbody>

                            </table>

                        </div>

                    </div>

                </div>


                <div className="row g-4">

                    <div className="col-lg-7">

                        <div className="card shadow-sm border-0">

                            <div className="card-header bg-white">
                                <strong>Notes</strong>
                            </div>

                            <div className="card-body">

                                <textarea
                                    className="form-control"
                                    rows="5"
                                    placeholder="Payment terms, bank details, thank you note etc."
                                    value={notes}
                                    onChange={e =>
                                        setNotes(e.target.value)
                                    }
                                />

                            </div>

                        </div>

                    </div>


                    <div className="col-lg-5">

                        <div className="card shadow-sm border-0">

                            <div className="card-header bg-white">
                                <strong>Invoice Summary</strong>
                            </div>

                            <div className="card-body">

                                <div className="d-flex justify-content-between mb-2">
                                    <span>Subtotal</span>
                                    <strong>
                                        ₹ {totals.subtotal.toFixed(2)}
                                    </strong>
                                </div>

                                <div className="d-flex justify-content-between mb-2">
                                    <span>Tax</span>
                                    <strong>
                                        ₹ {totals.tax.toFixed(2)}
                                    </strong>
                                </div>

                                <div className="mb-3">

                                    <label className="form-label">
                                        Discount
                                    </label>

                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        className="form-control"
                                        value={discount}
                                        onChange={e =>
                                            setDiscount(e.target.value)
                                        }
                                    />

                                </div>

                                <hr />

                                <div className="d-flex justify-content-between align-items-center">

                                    <h5 className="fw-bold mb-0">
                                        Grand Total
                                    </h5>

                                    <h4 className="fw-bold mb-0">
                                        ₹ {totals.total.toFixed(2)}
                                    </h4>

                                </div>

                                <button
                                    type="submit"
                                    className="btn btn-primary w-100 mt-4 py-2"
                                    disabled={saving}
                                >
                                    {saving
                                        ? "Saving Invoice..."
                                        : "Save Invoice"}
                                </button>

                            </div>

                        </div>

                    </div>

                </div>

            </form>

        </div>
    );
}
